/**
 * Export d'un tableau vers CSV, Excel ou PDF.
 *
 * Pourquoi les trois : un CSV s'ouvre en **une seule colonne** dans un Excel
 * configuré en français (il attend `;` comme séparateur, la norme dit `,`), et
 * l'organisateur se retrouve à devoir « convertir en tableau » à la main. Le
 * `.xlsx` est la réponse à ça — le CSV reste pour qui l'importe ailleurs, et le
 * PDF pour qui imprime la liste et l'affiche au mur.
 *
 * Les trois écrivent la MÊME matrice (en-têtes + lignes) : ce que l'organisateur
 * voit à l'écran est ce qu'il retrouve dans le fichier, quel que soit le format.
 *
 * Coût au chargement : `xlsx` et `jspdf` sont DÉJÀ dans le bundle principal
 * (la page Inscriptions et l'export PDF des équipes les importent), les prendre
 * ici ne coûte rien. `jspdf-autotable` est la seule dépendance neuve : chargée à
 * la demande, elle reste hors du premier écran de ceux qui n'exportent jamais
 * (mesuré : +1,5 ko sur le bundle principal au lieu de +11,7).
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface TableExportData {
  /** Nom du fichier, SANS extension ni date : les deux sont ajoutés ici. */
  filename: string;
  /** Titre imprimé en tête du PDF (ex. « Bénévoles »). */
  title: string;
  /** Ligne de contexte sous le titre (ex. le nom de l'événement). */
  subtitle?: string;
  headers: string[];
  rows: string[][];
}

/** `Rox For Fun 2026` → `rox-for-fun-2026`. */
export const slugify = (value: string): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // diacritiques laissés par NFD
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'export';

const todayStamp = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const fullName = (data: TableExportData, ext: string): string =>
  `${slugify(data.filename)}-${todayStamp()}.${ext}`;

const downloadBlob = (blob: Blob, name: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

// ── CSV ──────────────────────────────────────────────────────────────────────
// Séparateur virgule et BOM UTF-8 : format d'échange standard, inchangé depuis
// toujours (des organisateurs l'importent peut-être ailleurs). Celui qui veut
// un tableau prêt à l'emploi prend le .xlsx.
const exportCsv = (data: TableExportData): void => {
  const escape = (s: string) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = [data.headers, ...data.rows]
    .map(row => row.map(escape).join(','))
    .join('\n');
  downloadBlob(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }), fullName(data, 'csv'));
};

// ── Excel ────────────────────────────────────────────────────────────────────
const exportXlsx = async (data: TableExportData): Promise<void> => {
  const matrix = [data.headers, ...data.rows];
  const ws = XLSX.utils.aoa_to_sheet(matrix);

  // Largeur de colonne dérivée du contenu réel : sans ça, tout arrive à la
  // largeur par défaut et les emails sont coupés.
  ws['!cols'] = data.headers.map((header, col) => {
    const longest = matrix.reduce(
      (max, row) => Math.max(max, String(row[col] ?? '').length),
      String(header).length
    );
    return { wch: Math.min(Math.max(longest + 2, 10), 48) };
  });

  // Filtres sur la ligne d'en-tête : c'est ce qui donne les petites flèches de
  // tri/filtre dans Excel — le « tableau » que l'organisateur cherchait à
  // fabriquer à la main.
  if (data.rows.length > 0) {
    const lastCol = XLSX.utils.encode_col(data.headers.length - 1);
    ws['!autofilter'] = { ref: `A1:${lastCol}${matrix.length}` };
  }

  const wb = XLSX.utils.book_new();
  // Excel refuse un nom d'onglet > 31 caractères ou contenant : \ / ? * [ ]
  const sheetName = data.title.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Export';
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fullName(data, 'xlsx'));
};

// ── PDF ──────────────────────────────────────────────────────────────────────
const exportPdf = async (data: TableExportData): Promise<void> => {
  const { default: autoTable } = await import('jspdf-autotable');

  // Paysage : ces listes portent une colonne par question du formulaire, elles
  // débordent d'un A4 portrait dès trois questions.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(data.title.toUpperCase(), margin, margin + 4);

  if (data.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(data.subtitle, margin, margin + 10);
    doc.setTextColor(0);
  }

  autoTable(doc, {
    head: [data.headers],
    body: data.rows,
    startY: margin + (data.subtitle ? 15 : 9),
    margin: { left: margin, right: margin, bottom: margin + 6 },
    theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: 1.8, overflow: 'linebreak', lineColor: [215, 215, 215], lineWidth: 0.1 },
    headStyles: { fillColor: [242, 242, 242], textColor: [25, 25, 25], fontStyle: 'bold' },
    // Un document imprimé se lit à plat : des lignes alternées aident à suivre
    // une ligne jusqu'au bout sur une table large.
    alternateRowStyles: { fillColor: [251, 251, 251] },
    didDrawPage: () => {
      const page = doc.getNumberOfPages();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(140);
      doc.text(
        new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }),
        margin,
        pageHeight - margin + 4
      );
      doc.text(`LogFit® · ${page}`, pageWidth - margin, pageHeight - margin + 4, { align: 'right' });
      doc.setTextColor(0);
    },
  });

  doc.save(fullName(data, 'pdf'));
};

export const exportTable = async (format: ExportFormat, data: TableExportData): Promise<void> => {
  if (format === 'csv') return exportCsv(data);
  if (format === 'xlsx') return exportXlsx(data);
  return exportPdf(data);
};
